import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { firestore, storage } from "../../..";
import { useFeedback } from "../../../providers/feedback/feedback";

export const HOME_PAGE_SETTINGS_COLLECTION = "homePageSettings";
export const HOME_PAGE_BANNERS_DOC_ID = "banners";
export const HOME_BANNER_MIN_IMAGES = 4;
export const HOME_BANNER_MAX_IMAGES = 6;

type HomeBannerSettings = {
  bannerImages: string[];
  updatedAt?: string;
};

const DEFAULT_BANNERS = [
  "/images/banners/banner_1.jpg",
  "/images/banners/banner_2.JPG",
  "/images/banners/banner_3.JPG",
  "/images/banners/banner_4.jpg",
];

export default function HomeBannerSettingsTab() {
  const { showSnackbar } = useFeedback();
  const [existingImages, setExistingImages] = useState<string[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let isMounted = true;

    async function loadBannerSettings() {
      try {
        const snapshot = await getDoc(
          doc(firestore, HOME_PAGE_SETTINGS_COLLECTION, HOME_PAGE_BANNERS_DOC_ID),
        );

        if (!isMounted) {
          return;
        }

        const savedImages = (snapshot.data() as HomeBannerSettings | undefined)?.bannerImages || [];
        setExistingImages(savedImages.length ? savedImages : DEFAULT_BANNERS);
      } catch (error) {
        console.error("Failed to load home banner settings", error);
        showSnackbar("Failed to load home banner settings");
        if (isMounted) {
          setExistingImages(DEFAULT_BANNERS);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadBannerSettings();

    return () => {
      isMounted = false;
    };
  }, [showSnackbar]);

  const totalImages = useMemo(
    () => existingImages.length + selectedFiles.length,
    [existingImages.length, selectedFiles.length],
  );

  function handleFileSelection(event: React.ChangeEvent<HTMLInputElement>) {
    const nextFiles = Array.from(event.target.files || []);
    const allowedSlots = HOME_BANNER_MAX_IMAGES - existingImages.length - selectedFiles.length;

    if (allowedSlots <= 0) {
      showSnackbar(`You can keep at most ${HOME_BANNER_MAX_IMAGES} homepage banners`);
      return;
    }

    const acceptedFiles = nextFiles.slice(0, allowedSlots);

    if (acceptedFiles.length < nextFiles.length) {
      showSnackbar(`Only ${allowedSlots} more banner image(s) can be added`);
    }

    setSelectedFiles((prev) => [...prev, ...acceptedFiles]);
    event.target.value = "";
  }

  function handleRemoveExistingImage(index: number) {
    setExistingImages((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
  }

  function handleRemoveSelectedFile(index: number) {
    setSelectedFiles((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
  }

  async function handleSave() {
    if (totalImages < HOME_BANNER_MIN_IMAGES || totalImages > HOME_BANNER_MAX_IMAGES) {
      showSnackbar(
        `Please keep between ${HOME_BANNER_MIN_IMAGES} and ${HOME_BANNER_MAX_IMAGES} banner images`,
      );
      return;
    }

    try {
      setIsSaving(true);

      const uploadedUrls = await Promise.all(
        selectedFiles.map(async (file) => {
          const storageRef = ref(
            storage,
            `home_banners/${Date.now()}-${file.name.replace(/\s+/g, "-")}`,
          );
          await uploadBytes(storageRef, file);
          return getDownloadURL(storageRef);
        }),
      );

      const bannerImages = [...existingImages, ...uploadedUrls];

      await setDoc(
        doc(firestore, HOME_PAGE_SETTINGS_COLLECTION, HOME_PAGE_BANNERS_DOC_ID),
        {
          bannerImages,
          updatedAt: new Date().toISOString(),
        },
        { merge: true },
      );

      setExistingImages(bannerImages);
      setSelectedFiles([]);
      showSnackbar("Homepage banners updated successfully");
    } catch (error) {
      console.error("Failed to save homepage banners", error);
      showSnackbar("Failed to save homepage banners");
    } finally {
      setIsSaving(false);
    }
  }

  if (isLoading) {
    return (
      <Box mt={3}>
        <Typography>Loading homepage banners...</Typography>
      </Box>
    );
  }

  return (
    <Box mt={3}>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Manage the home page banner carousel here. Keep at least {HOME_BANNER_MIN_IMAGES}
        {" "}images and no more than {HOME_BANNER_MAX_IMAGES} images.
      </Typography>

      <Box mb={3}>
        <input type="file" accept="image/*" multiple onChange={handleFileSelection} />
      </Box>

      <Grid container spacing={3}>
        {existingImages.map((image, index) => (
          <Grid item xs={12} sm={6} md={4} key={`${image}-${index}`}>
            <Card sx={{ borderRadius: 3 }}>
              <CardContent>
                <Box position="relative">
                  <Box
                    component="img"
                    src={image}
                    alt={`Banner ${index + 1}`}
                    sx={{
                      width: "100%",
                      height: 180,
                      objectFit: "cover",
                      borderRadius: 2,
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveExistingImage(index)}
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      bgcolor: "rgba(0,0,0,0.55)",
                      color: "#fff",
                    }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Box>
                <Typography mt={1.5} variant="body2" color="text.secondary">
                  Current banner image {index + 1}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}

        {selectedFiles.map((file, index) => (
          <Grid item xs={12} sm={6} md={4} key={`${file.name}-${index}`}>
            <Card sx={{ borderRadius: 3, border: "1px dashed rgba(15,23,42,0.2)" }}>
              <CardContent>
                <Box position="relative">
                  <Box
                    component="img"
                    src={URL.createObjectURL(file)}
                    alt={file.name}
                    sx={{
                      width: "100%",
                      height: 180,
                      objectFit: "cover",
                      borderRadius: 2,
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveSelectedFile(index)}
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      bgcolor: "rgba(0,0,0,0.55)",
                      color: "#fff",
                    }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Box>
                <Typography mt={1.5} variant="body2" color="text.secondary">
                  Ready to upload
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box mt={3} display="flex" alignItems="center" gap={2} flexWrap="wrap">
        <Button variant="contained" onClick={handleSave} disabled={isSaving}>
          {isSaving ? "Saving..." : "Save Home Page Banners"}
        </Button>
        <Typography variant="body2" color="text.secondary">
          Total selected: {totalImages}
        </Typography>
      </Box>
    </Box>
  );
}
